import React, { useMemo, useState } from 'react';
import { Link } from 'components/Link/Link';
import { type Venue } from 'generated/graphql';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { DOSHII_MENU_NAME_INDEX } from 'consts';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { UNCATEGORISED_CATEGORY_MMID } from 'utils/constants';
import { useLocation } from 'react-router-dom';
import styles from './AllMenuHours.module.scss';

type MenuHoursAppCollection = Record<
  string,
  Record<string, { appName: string; appIcon: string; menus: { link: string; label: string }[] }>
>;

type GenerateMenuHoursAppCollectionReturnType = {
  menuHoursAppCollection: MenuHoursAppCollection;
  hasTimedMenu: boolean;
};

const { OPERATING_HOURS_WHITELISTED_APP_IDS } = getEnvironmentVariables();

// Function to generate the menuHoursAppCollection and check venue has timed menu
export const generateMenuHoursAppCollection = (currentVenue?: Venue): GenerateMenuHoursAppCollectionReturnType => {
  let hasTimedMenu = false;
  const menuHoursAppCollection =
    currentVenue?.menus?.reduce<MenuHoursAppCollection>((acc, menu) => {
      const app = menu.apps?.[0]; // A menu only ever has 1 app
      const appId = Number(app.id.startsWith('virtual.') ? app.id.split('.')[1] : app.id);
      if (!app || !OPERATING_HOURS_WHITELISTED_APP_IDS.includes(appId)) {
        return acc;
      }

      const entity = app.brand?.name || currentVenue.name;
      const appName = app.name;
      const appIcon = app.image || '';

      if (!acc[entity]) {
        acc[entity] = {};
      }

      if (!acc[entity][appName]) {
        acc[entity][appName] = {
          appName,
          appIcon,
          menus: []
        };
      }

      const [doshiiMenus, hasPrimaryMenu] = menu.categories?.length
        ? menu.categories.reduce<[string[], boolean]>(
            (acc, category) => {
              if (category.category.startsWith('doshii:menu:')) {
                const doshiiMenuName = category.category.split(':')[DOSHII_MENU_NAME_INDEX];
                if (!acc[0].includes(doshiiMenuName)) {
                  acc[0].push(doshiiMenuName);
                }
              } else if (category.mmId !== UNCATEGORISED_CATEGORY_MMID) {
                acc[1] = true;
              }
              return acc;
            },
            [[], false]
          )
        : [[], false];

      // Add a primary menu when a primary menu exists or when there are no doshiiMenus
      if (hasPrimaryMenu || !doshiiMenus.length) {
        acc[entity][appName].menus.push({
          link: `/venue/${currentVenue?.hashedId}/menus/${menu.mmId}/hours`,
          label: `Main Menu`
        });
      }

      doshiiMenus.forEach((doshiiMenu) => {
        acc[entity][appName].menus.push({
          link: `/venue/${currentVenue?.hashedId}/menus/${menu.mmId}/hours/${encodeURI(doshiiMenu)}`,
          label: `${doshiiMenu} Menu`
        });

        // Set hasTimedMenu to true if any timed menu exists in any of the entity's any app
        hasTimedMenu = true;
      });

      return acc;
    }, {}) ?? {};

  return { menuHoursAppCollection, hasTimedMenu };
};

type Props = {
  currentVenue?: Venue;
};

const AllMenuHours: React.FC<Props> = ({ currentVenue }) => {
  const [openApps, setOpenApps] = useState<Record<string, boolean>>({});

  const location = useLocation();
  const redirectParam = `?redirectTo=${location.pathname}`;

  const { menuHoursAppCollection, hasTimedMenu } = useMemo(
    () => generateMenuHoursAppCollection(currentVenue),
    [currentVenue]
  );

  if (Object.keys(menuHoursAppCollection).length === 0) {
    return (
      <div className={styles.NoMenuHours}>
        <p>No supported apps subscribed.</p>
      </div>
    );
  }

  return (
    <div className={styles.AllMenuHours}>
      <ul className={styles.EntityList}>
        {Object.entries(menuHoursAppCollection).map(([entity, apps]) => (
          <li key={entity} className={styles.Entity}>
            {Object.keys(menuHoursAppCollection).length > 1 && <h4 className={styles.BrandHeader}>{entity}:</h4>}
            <ul className={styles.AppList}>
              {Object.entries(apps).map(([appName, appDetails]) => (
                <li key={appName} className={styles.App}>
                  {hasTimedMenu ? (
                    <button
                      onClick={() => {
                        setOpenApps((prevState) => ({
                          ...prevState,
                          [`${entity}-${appName}`]: !prevState[`${entity}-${appName}`]
                        }));
                      }}
                      className={`${styles.Toggle} ${openApps[`${entity}-${appName}`] ? styles.Open : ''}`}
                      type="button"
                      tabIndex={0}
                      data-testid={`${appName}-menulinks-toggle`}
                      aria-label="expand or collapse menu hours links"
                    >
                      <div className={styles.AppHeader}>
                        <img src={appDetails.appIcon} alt={`Icon for ${appDetails.appName}`} />
                        <h4>{appDetails.appName}</h4>
                        <i className="mm-angle-down" />
                      </div>
                    </button>
                  ) : (
                    <Link
                      href={`${appDetails.menus[0].link}${redirectParam}`}
                      className={styles.Link}
                      onClick={() => {
                        logAmplitudeEvent({
                          name: `open app menu`,
                          category: AmplitudeEventCategories.MM_SIDE_PANEL_ALL_ITEMS_OPERATING_HOURS,
                          properties: {
                            [AmplitudeEventProperties.VENUE_ID]: currentVenue?.hashedId || '',
                            [AmplitudeEventProperties.ENTITY]: entity,
                            [AmplitudeEventProperties.APP]: appName,
                            [AmplitudeEventProperties.MENU_NAME]: 'main'
                          }
                        });
                      }}
                    >
                      <div className={styles.AppHeader}>
                        <img src={appDetails.appIcon} alt={`Icon for ${appDetails.appName}`} />
                        <h4>{appDetails.appName}</h4>
                        <i className="mm-angle-right" />
                      </div>
                    </Link>
                  )}

                  {hasTimedMenu && (
                    <div className={`${styles.AppContent} ${openApps[`${entity}-${appName}`] ? styles.Open : ''}`}>
                      <ul className={`${styles.MenuList} ${openApps[appName] ? styles.Open : ''}`}>
                        {appDetails.menus.length
                          ? appDetails.menus.map((menuApp) => (
                              <li key={menuApp.label} className={styles.Menu}>
                                <Link
                                  href={`${menuApp.link}${redirectParam}`}
                                  className={styles.Link}
                                  onClick={() => {
                                    logAmplitudeEvent({
                                      name: `open app menu`,
                                      category: AmplitudeEventCategories.MM_SIDE_PANEL_ALL_ITEMS_OPERATING_HOURS,
                                      properties: {
                                        [AmplitudeEventProperties.VENUE_ID]: currentVenue?.hashedId || '',
                                        [AmplitudeEventProperties.ENTITY]: entity,
                                        [AmplitudeEventProperties.APP]: appName,
                                        [AmplitudeEventProperties.MENU_NAME]: menuApp.label
                                      }
                                    });
                                  }}
                                >
                                  <p className={styles.Text}>{`${menuApp.label} hours`}</p>
                                  <i className="mm-angle-right" />
                                </Link>
                              </li>
                            ))
                          : 'No menus available'}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AllMenuHours;
